import React, { useEffect, useState } from 'react';
import SearchIcon from '@atlaskit/icon/glyph/search';
import './search.less';
import analytics from '../../utils/analytics/analytics';
import { SEARCH_API } from '../../utils/constants';

interface LegacyProps {
    productName?: string;
    productVersion?: string;
}

interface SearchProps {
    onSearch: (searchValue: string, legacyProps?: LegacyProps) => void;
    className?: string;
    placeholder?: string;
    initialValue?: string;
    analyticsMetadata: {
        parentComponent: string;
        [key: string]: unknown;
    };
    legacyProps?: LegacyProps;
}

interface TrackEventProps {
    action: string;
    component: string;
    metadata?: Record<string, unknown>;
}

// TODO: Replace usages of this with handleOnSearch once the new search is enabled (FF: isSearchEnabled)
export const handleOnSearchLegacy = (
    searchValue: string,
    legacyProps?: LegacyProps
) => {
    const productName = legacyProps?.productName;
    const productVersion = legacyProps?.productVersion;

    const productNameQuery = productName
        ? `&productName=${productName.replace(/\s/g, '+')}`
        : '';
    const productVersionQuery = productVersion
        ? `&productVersion=${productVersion}`
        : '';

    const encodedUrl = `${SEARCH_API}?query=${encodeURIComponent(
        searchValue
    )}${productNameQuery}${productVersionQuery}`;

    // @ts-ignore
    if (window.Cypress) {
        window.open(encodedUrl, '_self');
    } else {
        window.open(encodedUrl, '_blank');
    }

    trackAnalyticsEvent({
        action: 'redirect',
        component: 'search',
        metadata: {
            searchValue,
            redirectTo: 'CAC search',
            redirectToUrl: encodedUrl,
            ...legacyProps
        }
    });
};

export const handleOnSearch = (searchValue: string) => {
    const encodedUrl = `/search-results/?searchTerm=${encodeURIComponent(
        searchValue
    )}`;

    window.open(encodedUrl, '_self');

    trackAnalyticsEvent({
        action: 'redirect',
        component: 'search',
        metadata: {
            searchValue,
            redirectTo: 'SAC search',
            redirectToUrl: encodedUrl
        }
    });
};

const trackAnalyticsEvent = ({
    action,
    component,
    metadata
}: TrackEventProps) => {
    analytics.trackEvent('search', {
        // @ts-ignore
        event: action,
        eventComponent: component,
        actionSubjectId: 'searchField',
        category: 'search',
        action,
        label: 'search field',
        ...metadata
    });
};

export const Search = ({
    onSearch,
    placeholder = '',
    initialValue = '',
    className,
    analyticsMetadata,
    legacyProps = {}
}: SearchProps) => {
    const [value, setValue] = useState<string>('');
    const [initialSearchTriggered, setInitialSearchTriggered] =
        useState<boolean>(false);

    useEffect(() => {
        if (initialValue && !initialSearchTriggered) {
            setValue(initialValue);
            onSearch(initialValue);
            setInitialSearchTriggered(true);
        }
    }, [initialValue, initialSearchTriggered, onSearch]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setValue(value);
    };

    const handleKeyUp = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            trackAnalyticsEvent({
                action: 'entered',
                component: 'searchInput',
                metadata: {
                    ...analyticsMetadata,
                    ...legacyProps,
                    searchValue: value
                }
            });
            onSearch(value, legacyProps);
        }
    };

    const handleOnClick = () => {
        trackAnalyticsEvent({
            action: 'clicked',
            component: 'searchIcon',
            metadata: {
                ...analyticsMetadata,
                ...legacyProps,
                searchValue: value
            }
        });
        onSearch(value, legacyProps);
    };

    const handleOnFocus = () => {
        trackAnalyticsEvent({
            action: 'focused',
            component: 'searchInput',
            metadata: {
                ...analyticsMetadata,
                ...legacyProps
            }
        });
    };

    const classes = ['search'];
    if (className) {
        classes.push(className);
    }

    return (
        <div className={classes.join(' ')}>
            <input
                data-testid="search-input"
                aria-label="Search site content"
                className="search__input"
                type="search"
                name="query"
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onKeyUp={handleKeyUp}
                onFocus={handleOnFocus}
            />
            <div
                data-testid="search-icon"
                className="search__icon"
                role="button"
                aria-label="Search site content"
                tabIndex={0}
                onClick={handleOnClick}
                onKeyUp={handleKeyUp}
            >
                <SearchIcon label="Search" />
            </div>
        </div>
    );
};
